<template>
  <div class="api-key-management">
    <Sidebar />
    
    <div class="main-content">
      <!-- Warning Banner -->
      <div class="warning-banner">
        <div class="warning-content">
          <div class="warning-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 16Q10.1875 15.96875 12 14.9375Q13.8125 13.875 14.9375 12Q16 10.09375 16 8Q16 5.90625 14.9375 4Q13.8125 2.125 12 1.0625Q10.1875 0.03125 8 0Q5.8125 0.03125 4 1.0625Q2.1875 2.125 1.0625 4Q0 5.90625 0 8Q0 10.09375 1.0625 12Q2.1875 13.875 4 14.9375Q5.8125 15.96875 8 16ZM8 4Q8.6875 4.0625 8.75 4.75L8.75 8.25Q8.6875 8.9375 8 9Q7.3125 8.9375 7.25 8.25L7.25 4.75Q7.3125 4.0625 8 4ZM7 11Q7 10.5625 7.28125 10.28125Q7.5625 10 8 10Q8.4375 10 8.71875 10.28125Q9 10.5625 9 11Q9 11.4375 8.71875 11.71875Q8.4375 12 8 12Q7.5625 12 7.28125 11.71875Q7 11.4375 7 11Z" fill="#FACC15"/>
            </svg>
          </div>
          <div class="warning-text">
            <p>API keys are credentials for accessing Cloudsway API. Please keep them secure.</p>
          </div>
        </div>
      </div>

      <!-- Page Content -->
      <div class="page-content">
        <!-- Header -->
        <div class="page-header">
          <h1>API Key</h1>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading API key information...</p>
        </div>

        <!-- API Key Card -->
        <div v-else class="api-key-card">
          <div class="card-header">
            <div class="card-info">
              <div class="key-icon">
                <i class="fas fa-key"></i>
              </div>
              <div class="card-text">
                <h3>{{ apiKey.description }}</h3>
                <span class="created-date">Created {{ apiKey.createdAt }}</span>
              </div>
            </div>
            <button class="edit-btn" @click="editApiKey" title="Edit API Key" :disabled="loading">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" fill="none"/>
              </svg>
            </button>
          </div>
          
          <div class="card-content">
            <div class="key-section">
              <div class="key-header">
                <label class="field-label">API Key</label>
                <div class="key-status">
                  <span class="status-badge">Active</span>
                </div>
              </div>
              <div class="key-display-container">
                <div class="key-value-wrapper">
                  <span class="key-value">{{ apiKey.showKey ? apiKey.key : '••••••••••••••••••••••••••••••••' }}</span>
                </div>
                <div class="key-actions">
                  <button class="action-btn" @click="toggleKeyVisibility" :title="apiKey.showKey ? 'Hide key' : 'Show key'">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2" fill="none"/>
                      <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" fill="none"/>
                    </svg>
                  </button>
                  <button class="action-btn" @click="copyKey" title="Copy key">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" stroke-width="2" fill="none"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <!-- Edit API Key Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Edit API Key</h3>
          <button class="modal-close" @click="closeEditModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#6B7280" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Description</label>
            <input 
              v-model="editingApiKey.description" 
              type="text" 
              class="form-input"
              placeholder="Enter description..."
              maxlength="100"
            />
          </div>
          
          <div class="form-group">
            <label class="form-label">API Key</label>
            <div class="api-key-display">
              <input 
                :value="editingApiKey.key" 
                type="text" 
                class="form-input key-input"
                readonly
              />
              <button class="refresh-btn" @click="refreshApiKey" title="Generate new API key" :disabled="loading">
                <svg v-if="!loading" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M23 4v6h-6M1 20v-6h6" stroke="#2563EB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="#2563EB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <div v-else class="loading-spinner-small"></div>
              </button>
            </div>
            <p class="refresh-warning">
              Clicking refresh will generate a new key and the old key will be invalidated.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="cancelEdit">Cancel</button>
          <button class="btn-primary" @click="confirmEditApiKey" :disabled="!editingApiKey.description.trim()">
            {{ hasRefreshedKey ? 'Save & Close' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import Sidebar from '@/components/Sidebar.vue'
import { handleTokenExpired, isTokenExpiredError, handleApiError } from '@/utils/auth.js'

export default {
  name: 'ApiKeyManagement',
  components: {
    Sidebar
  },
  setup() {
    const apiKey = ref({
      key: '',
      description: 'My API Key',
      createdAt: '',
      showKey: false
    })

    const loading = ref(false)
    const customerCode = ref('')
    const showEditModal = ref(false)
    const hasRefreshedKey = ref(false)
    
    // 防抖和限流控制
    const lastFetchTime = ref(0)
    const isRequestInProgress = ref(false)
    const FETCH_COOLDOWN = 2000 // 2秒内不允许重复请求
    
    const editingApiKey = reactive({
      description: '',
      key: '',
      originalKey: ''  // 保存原始key用于判断是否真的刷新了
    })

    // API 请求工具函数
    const request = async (url, options = {}) => {
      // 获取最新的用户信息和token
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      const token = userInfo.token || ''
      
      const defaultOptions = {
        headers: {
          'Content-Type': 'application/json',
          // 同时使用Authorization header和cookie确保认证成功
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        credentials: 'include',
        ...options
      }

      const MAX_RETRIES = 3
      let lastError

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          console.log(`API Request attempt ${attempt}: /api${url}`)
          
          // 使用 /api 前缀，通过 nginx 代理到 5001 端口
          const response = await fetch(`/api${url}`, {
            ...defaultOptions,
            // 增加超时控制
            signal: AbortSignal.timeout(30000) // 30秒超时
          })
          
          if (!response.ok) {
            // 检查是否是认证相关的错误状态码
            if (response.status === 401 || response.status === 403) {
              console.warn('Token expired (401/403), redirecting to login...')
              handleTokenExpired()
              throw new Error('Authentication expired. Please login again.')
            }
            
            // 检查是否是500错误，可能是token过期
            if (response.status === 500) {
              try {
                const errorData = await response.json()
                // 检查500错误中是否包含token过期信息
                if (isTokenExpiredError({ message: errorData.message }, errorData)) {
                  console.warn('Token expired (500 with token error), redirecting to login...')
                  handleTokenExpired()
                  throw new Error('Authentication expired. Please login again.')
                }
                // 如果不是token过期，继续抛出原始错误
                throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`)
              } catch (parseError) {
                // 如果解析JSON失败，抛出原始500错误
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
              }
            }
            
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }
          
          const data = await response.json()
          
          // 检查后端返回的错误代码，使用工具函数判断token过期
          if (data.code !== 0) {
            if (isTokenExpiredError({ message: data.message }, data)) {
              console.warn('Token expired based on error message:', data.message)
              handleTokenExpired()
              throw new Error('Authentication expired. Please login again.')
            }
            throw new Error(data.message || 'Request failed')
          }

          console.log(`API Request success on attempt ${attempt}`)
          return data
        } catch (error) {
          lastError = error
          console.error(`API Request attempt ${attempt} failed:`, error.message)
          
          // 如果是token过期错误，不进行重试
          if (error.message.includes('Authentication expired')) {
            throw error
          }
          
          // 如果是最后一次尝试，直接抛出错误
          if (attempt === MAX_RETRIES) {
            break
          }
          
          // 等待后重试 (指数退避)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000)
          console.log(`Retrying in ${delay}ms...`)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
      
      // 使用工具函数处理最终错误
      try {
        handleApiError(lastError)
      } catch (processedError) {
        throw processedError
      }
    }

    // 获取账户信息和API Key（带防抖和缓存）
    const fetchAccountInfo = async (forceRefresh = false) => {
      const now = Date.now()
      
      // 防止频繁请求
      if (!forceRefresh && (now - lastFetchTime.value < FETCH_COOLDOWN)) {
        console.log(`Request blocked: Too frequent (cooldown: ${FETCH_COOLDOWN}ms)`)
        return
      }
      
      // 防止并发请求
      if (isRequestInProgress.value) {
        console.log('Request blocked: Another request is in progress')
        return
      }
      
      try {
        isRequestInProgress.value = true
        loading.value = true
        lastFetchTime.value = now
        
        console.log('Fetching account info...')
        const response = await request('/is/account/info')
        
        const accountData = response.data
        apiKey.value.key = accountData.ak || ''
        apiKey.value.description = accountData.loginName || 'My API Key'
        customerCode.value = accountData.customerCode || ''
        apiKey.value.createdAt = new Date().toLocaleDateString()
        
        console.log('Account info fetched successfully')
        
      } catch (error) {
        console.error('Failed to fetch account info:', error)
        
        // 如果是token过期错误，不显示额外的alert（因为handleTokenExpired已经处理了）
        if (error.message.includes('Authentication expired')) {
          return // 直接返回，不显示错误提示
        }
        
        // 根据错误类型提供不同的用户提示
        if (error.message.includes('Backend service is unavailable')) {
          alert('⚠️ Backend service is temporarily unavailable.\n\nThis usually means:\n• The API server needs to be restarted\n• Network connectivity issues\n\nPlease try again in a few minutes or contact support.')
        } else if (error.message.includes('timeout')) {
          alert('⏱️ Request timeout. Please check your network connection and try again.')
        } else {
          alert('❌ Failed to load API key information:\n' + error.message + '\n\nPlease refresh the page or try again later.')
        }
      } finally {
        loading.value = false
        isRequestInProgress.value = false
      }
    }

    // 刷新API Key
    const refreshApiKey = async () => {
      if (!customerCode.value) {
        alert('Customer code not available')
        return
      }

      try {
        loading.value = true
        const response = await request('/ak/update', {
          method: 'POST',
          body: JSON.stringify({
            customer_code: customerCode.value
          })
        })
        
        // 更新编辑框中的API key
        editingApiKey.key = response.data.newAk
        // 同时更新主显示的API key
        apiKey.value.key = response.data.newAk
        
        // 标记已经刷新了key
        hasRefreshedKey.value = true
        
        alert('API key refreshed successfully!')
        
      } catch (error) {
        console.error('Failed to refresh API key:', error)
        alert('Failed to refresh API key: ' + error.message)
      } finally {
        loading.value = false
      }
    }

    const toggleKeyVisibility = () => {
      apiKey.value.showKey = !apiKey.value.showKey
    }

    const copyKey = async () => {
      try {
        await navigator.clipboard.writeText(apiKey.value.key)
        alert('API key copied to clipboard!')
      } catch (err) {
        console.error('Failed to copy key:', err)
        alert('Failed to copy API key')
      }
    }

    const editApiKey = () => {
      // 保存原始值用于取消时恢复
      editingApiKey.description = apiKey.value.description
      editingApiKey.key = apiKey.value.key
      editingApiKey.originalKey = apiKey.value.key  // 保存原始key
      hasRefreshedKey.value = false  // 重置刷新状态
      showEditModal.value = true
    }

    // 取消编辑（恢复原始值）
    const cancelEdit = () => {
      // 如果已经刷新了key，提示用户
      if (hasRefreshedKey.value) {
        if (!confirm('You have refreshed the API key. The new key has been saved and cannot be reverted. Do you want to close without saving the description changes?')) {
          return
        }
      }
      
      // 如果key被刷新了，即使取消也要保留新的key（因为服务端已经更新）
      // 但description恢复原始值
      if (!hasRefreshedKey.value) {
        // 如果没有刷新key，完全恢复
        apiKey.value.key = editingApiKey.originalKey
      }
      
      closeEditModal()
    }
    
    const closeEditModal = () => {
      showEditModal.value = false
      editingApiKey.description = ''
      editingApiKey.key = ''
      editingApiKey.originalKey = ''
      hasRefreshedKey.value = false  // 重置刷新状态
    }

    const confirmEditApiKey = () => {
      // 保存description的修改
      apiKey.value.description = editingApiKey.description
      // key已经在刷新时实时更新了，这里不需要再更新
      
      if (hasRefreshedKey.value) {
        alert('Changes saved! Your new API key is active.')
      }
      
      closeEditModal()
    }

    // 组件挂载时获取数据
    onMounted(() => {
      fetchAccountInfo()
    })

    return {
      apiKey,
      loading,
      toggleKeyVisibility,
      copyKey,
      editApiKey,
      showEditModal,
      editingApiKey,
      hasRefreshedKey,
      cancelEdit,
      closeEditModal,
      confirmEditApiKey,
      refreshApiKey
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.api-key-management {
  min-height: 100vh;
  display: flex;
  background: #F9FAFB;
  color: #111827;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  transition: margin-left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

// Warning Banner
.warning-banner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 16px 0 16px;

  .warning-content {
    background: #FEFCE8;
    border: none;
    border-left: 4px solid #FACC15;
    border-radius: 0;
    padding: 16px 20px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    box-shadow: 0px 1px 2px -1px rgba(0, 0, 0, 0.1), 0px 1px 3px 0px rgba(0, 0, 0, 0.1);
    
    .warning-icon {
      flex-shrink: 0;
      margin-top: 2px;
    }
    
    .warning-text {
      flex: 1;
      
      p {
        margin: 0;
        font-size: 14px;
        line-height: 20px;
        color: #A16207;
        font-weight: 400;
      }
    }
  }
}

// Page Content
.page-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  flex: 1;
  width: 100%;
}

// Page Header
.page-header {
  padding: 24px 16px 24px 16px;
  margin-top: 40px;

  h1 {
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    margin: 0;
    color: #111827;
  }
}

// API Key Card
.api-key-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin: 0 16px 24px 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    border-color: #cbd5e1;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  
  .card-info {
    display: flex;
    align-items: center;
    gap: 16px;
    
    .key-icon {
      width: 48px;
      height: 48px;
      background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      
      i {
        font-size: 20px;
        color: #2563EB;
      }
    }
    
    .card-text {
      h3 {
        font-size: 18px;
        font-weight: 600;
        color: #1e293b;
        margin: 0 0 4px 0;
        line-height: 1.4;
      }
      
      .created-date {
        font-size: 13px;
        color: #64748b;
        font-weight: 500;
      }
    }
  }
  
  .edit-btn {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #475569;
    
    &:hover {
      background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
      border-color: #cbd5e1;
      transform: translateY(-1px);
      color: #334155;
    }
  }
}

.card-content {
  padding: 20px 24px 24px 24px;
}

.key-section {
  .key-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    
    .field-label {
      font-size: 14px;
      font-weight: 600;
      color: #374151;
      margin: 0;
    }
    
    .key-status {
      .status-badge {
        background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
        color: #166534;
        font-size: 12px;
        font-weight: 600;
        padding: 4px 12px;
        border-radius: 20px;
        border: 1px solid #bbf7d0;
      }
    }
  }
}

.key-display-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #cbd5e1;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  }
  
  .key-value-wrapper {
    flex: 1;
    
    .key-value {
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 14px;
      color: #1e293b;
      font-weight: 500;
      word-break: break-all;
      line-height: 1.5;
    }
  }
  
  .key-actions {
    display: flex;
    gap: 8px;
    
    .action-btn {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      color: #64748b;
      
      &:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
        color: #475569;
        transform: translateY(-1px);
      }
    }
  }
}


// Icon Button
.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #F3F4F6;
  }
  
  &.delete-btn:hover {
    background: #FEF2F2;
  }
  
  svg {
    flex-shrink: 0;
  }
}


// Modal Styles
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
  
  .modal-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: background-color 0.2s ease;
    
    &:hover {
      background: #F3F4F6;
    }
  }
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 24px 24px;
}

// Form Styles
.form-group {
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  color: #374151;
  font-size: 14px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 400;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2563EB;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }
}

.form-hint {
  font-size: 12px;
  color: #6B7280;
  margin-top: 6px;
  margin-bottom: 0;
}

// Button Styles
.btn-primary {
  padding: 10px 20px;
  background: #2563EB;
  border: 1px solid #2563EB;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #1D4ED8;
    border-color: #1D4ED8;
  }

  &:disabled {
    background: #9CA3AF;
    border-color: #9CA3AF;
    cursor: not-allowed;
  }
}

.btn-secondary {
  padding: 10px 20px;
  background: white;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #F3F4F6;
    border-color: #9CA3AF;
  }
}

.btn-danger {
  padding: 10px 20px;
  background: #EF4444;
  border: 1px solid #EF4444;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #DC2626;
    border-color: #DC2626;
  }

  &:disabled {
    background: #9CA3AF;
    border-color: #9CA3AF;
    cursor: not-allowed;
  }
}

// Warning Section (for delete modal)
.warning-section {
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
}

.warning-icon-large {
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
}

.warning-text {
  font-size: 14px;
  color: #7F1D1D;
  margin-bottom: 12px;
  line-height: 1.5;
}

.api-key-info {
  background: white;
  border: 1px solid #FECACA;
  border-radius: 6px;
  padding: 12px;
  font-size: 13px;
  color: #7F1D1D;
  text-align: left;
  word-break: break-all;
}

// Edit Modal Specific Styles
.api-key-display {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .key-input {
    flex: 1;
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    
    &:focus {
      border-color: #D1D5DB;
      box-shadow: none;
    }
  }
  
  .refresh-btn {
    padding: 12px;
    background: #EFF6FF;
    border: 1px solid #DBEAFE;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: #DBEAFE;
      border-color: #BFDBFE;
    }
    
    svg {
      display: block;
    }
  }
}

.refresh-warning {
  font-size: 12px;
  color: #DC2626;
  margin-top: 6px;
  margin-bottom: 0;
  font-style: italic;
}

// Loading Styles
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  
  p {
    margin-top: 16px;
    color: #6B7280;
    font-size: 14px;
  }
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f4f6;
  border-top: 3px solid #2563EB;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #2563EB;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Responsive Design
@media (max-width: 1400px) {
  .warning-banner,
  .page-content {
    max-width: calc(100vw - 320px); // 280px sidebar + 40px padding
  }
  
  .main-content.sidebar-collapsed {
    .warning-banner,
    .page-content {
      max-width: calc(100vw - 120px); // 80px collapsed sidebar + 40px padding
    }
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    
    .create-btn {
      justify-content: center;
    }
  }
  
  .api-keys-table {
    font-size: 12px;
    
    th, td {
      padding: 8px 12px;
    }
  }
  
  .key-cell,
  .actions-cell {
    gap: 4px;
  }

  .pagination-container {
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }

  .pagination {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>